/**
 * The confirm step shared by every curator decision.
 *
 * One component for both surfaces - the Settings queue and the published-signal
 * modal - so the reason rule, the conflict rule and the retry rule cannot drift
 * apart between them.
 *
 * Reason handling mirrors the server exactly: reject, unpublish_for_review and
 * unpublish_reject require one, and the value is trimmed before it is judged, so
 * whitespace never passes as a reason here and is then refused there.
 */

import { useState } from 'react';
import {
  normalizeReason,
  type SubmissionBlock,
} from '../decision-intent';
import type { CuratorApiError } from '../errors';
import { DECISION_LABELS, requiresReason, type DecisionKind } from '../types';

const MAX_REASON_LENGTH = 2000;

export function CuratorDecisionConfirm({
  kind,
  disabled = false,
  busy,
  error,
  conflicted,
  blockReason = null,
  refreshing = false,
  onCancel,
  onConfirm,
  onRefresh,
}: {
  kind: DecisionKind;
  /** True while any sibling decision is in flight; the surface shares one. */
  disabled?: boolean;
  /** True while THIS decision is in flight. */
  busy: boolean;
  error: CuratorApiError | null;
  conflicted: boolean;
  /** Why submission is refused, or null when it is open. */
  blockReason?: SubmissionBlock | null;
  /** True while the authoritative re-read that clears a block is running. */
  refreshing?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
  onRefresh: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const normalized = normalizeReason(reason);
  const needsReason = requiresReason(kind);
  const tooLong = normalized !== null && normalized.length > MAX_REASON_LENGTH;
  // Two different failures both close Confirm, for the same underlying reason:
  // resending THIS request cannot succeed.
  //   conflict       the ledger moved, so the expected version is stale;
  //   non_retryable  a 400/401/403/404 or an unclassifiable error, which the
  //                  server would simply repeat.
  // A network, relay, timeout or 5xx failure is NOT blocking: its outcome is
  // uncertain, so Confirm stays live and resends under the same key.
  const blocked = blockReason !== null || conflicted;
  const canConfirm =
    !busy &&
    !disabled &&
    !blocked &&
    !tooLong &&
    (!needsReason || normalized !== null);

  return (
    <div className="sx-decision-dialog-backdrop">
      <div
        className="sx-popover sx-decision-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Confirm ${DECISION_LABELS[kind]}`}
      >
      <div className="sx-popover-title">{DECISION_LABELS[kind]}?</div>

      <p className="sx-popover-help">
        {kind === 'approve'
          ? 'Approving publishes this signal to every user who can see the feed.'
          : kind === 'reopen'
            ? 'Reopening returns this signal to the review queue. It stays withheld from users.'
            : 'This withholds the signal from users. The decision and its reason are recorded permanently.'}
      </p>

      <label className="sx-eyebrow" htmlFor={`curator-reason-${kind}`}>
        {needsReason ? 'Reason (required)' : 'Reason (optional)'}
      </label>
      <textarea
        id={`curator-reason-${kind}`}
        className="sx-field"
        value={reason}
        disabled={busy}
        aria-required={needsReason}
        aria-invalid={tooLong}
        placeholder={
          needsReason ? 'Why is this being withheld?' : 'Optional note for the record'
        }
        onChange={(event) => setReason(event.target.value)}
      />

      {tooLong ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          Reason must be {MAX_REASON_LENGTH} characters or fewer.
        </p>
      ) : null}

      {needsReason && normalized === null && reason.length > 0 ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          A reason is required — whitespace alone is not a reason.
        </p>
      ) : null}

      {error ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          {error.message}
          {error.retryable && !blocked
            ? ' You can try again; the same decision is not applied twice.'
            : null}
          {blockReason === 'non_retryable'
            ? ' Sending this again would get the same answer. Refresh to start a new attempt, or change the decision.'
            : null}
        </p>
      ) : null}

      <div className="sx-popover-actions">
        <button
          type="button"
          className="sx-btn sx-btn-p3"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        {blocked ? (
          // The only way forward, for either block: re-read authoritative state.
          // Confirm stays unavailable until that read SUCCEEDS, so the button
          // also stays disabled while it is running rather than inviting a
          // second overlapping re-read.
          <button
            type="button"
            className="sx-cta"
            disabled={busy || refreshing}
            onClick={onRefresh}
          >
            {refreshing ? 'Refreshing…' : 'Refresh and start again'}
          </button>
        ) : (
          <button
            type="button"
            className="sx-cta"
            disabled={!canConfirm}
            onClick={() => onConfirm(normalized)}
          >
            {busy ? 'Recording…' : DECISION_LABELS[kind]}
          </button>
        )}
      </div>
    </div>
    </div>
  );
}
